import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { DialogActionsBar, Dialog as DialogComponent } from "@progress/kendo-react-dialogs";
import { Input } from "@progress/kendo-react-inputs";
import { button, label, notifyIcon, number, quote, tooltip } from "../../../config";
import { getNotification } from "../../../utils/common";

/**
 * Custom hook for handling click outside dialog
 */
const useClickOutsideDialog = (onDialogClose) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dialogElement = event.target.closest(".k-dialog");
      const dialogOverlay = event.target.closest(".k-overlay");

      if ((dialogOverlay && !dialogElement) || (!dialogElement && !dialogOverlay)) {
        onDialogClose(event);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onDialogClose]);
};

/**
 * Custom hook for focusing and selecting input on mount
 */
const useInputFocus = (inputRef) => {
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
};

/**
 * Utility function to get initial name by removing .dox extension if present
 */
const getInitialName = (name, isDox) => {
  if (isDox && name && name.toLowerCase().endsWith(label.DOX_EXT)) {
    return name.slice(0, -label.DOX_EXT.length);
  }
  return name;
};

/**
 * Reusable input component for dox files
 */
const DoxInput = ({ inputRef, value, onChange, maxLength, hasValidationError }) => (
  <div className={`dox-rename-input-wrapper ${hasValidationError ? "error" : ""}`}>
    <Input ref={inputRef} maxLength={maxLength} value={value} onChange={onChange} />
    <span className='dox-extension'>{label.DOX_EXT}</span>
  </div>
);

/**
 * Parent Component: FileManagerContent
 * A dialog for renaming a file. It allows users to input a new name for the file and confirm or cancel the action.
 *@author Muskan Thakur
 */
export const EditDialog = React.memo((props) => {
  const { editValue, onDialogClose } = props;
  const isDox = editValue.mimeType === label.DOX_MIME_TYPE;
  const [nameValue, setNameValue] = useState(getInitialName(editValue.name, isDox));
  const inputRef = useRef(null);
  const dialogRef = useRef(null);

  useClickOutsideDialog(onDialogClose);
  useInputFocus(inputRef);

  /**Handles the dialog button click. Passes the new file name and the action type (rename/cancel) to the parent component. */
  const handleDialogClick = (event) => {
    props.onDialogClick({
      event,
      path: props.editValue.path,
      value: typeof nameValue === "string" ? nameValue : nameValue.path,
      type: event.currentTarget.value,
    });
  };

  /**Handles changes to the input field. */
  const handleInputChange = (event) => {
    setNameValue(event.value);
  };

  const renderInput = () => {
    if (isDox) {
      return <DoxInput inputRef={inputRef} value={nameValue} onChange={handleInputChange} maxLength={number.FORTY} hasValidationError={false} />;
    }
    return <Input ref={inputRef} maxLength={number.FORTY} value={nameValue} className='edit-dialog-input k-textbox' onChange={handleInputChange} />;
  };

  return (
    <DialogComponent title={"Please confirm"} onClose={onDialogClose} ref={dialogRef}>
      <div className='edit-dialog-message m-0 mb-1 p-0 text-left'>{quote.NEW_FILE_NAME}</div>
      {renderInput()}
      <DialogActionsBar>
        <Button className="btn btn-ghost" value={"cancel"} themeColor='base' onClick={handleDialogClick}>
          {button.CANCEL}
        </Button>
        <Button className="btn btn-primary" value={"rename"} themeColor='primary' onClick={handleDialogClick}>
          {button.RENAME}
        </Button>
      </DialogActionsBar>
    </DialogComponent>
  );
});

/**
 * reusable Confirmation Dialog Component
 * handles delete, restore, and other confirmation actions.
 * @author Muskan Thakur
 */
export const ConfirmationDialog = React.memo(({ title, message, confirmLabel, confirmValue, onDialogClick, onDialogClose }) => {
  const handleDialogClick = (event) => {
    onDialogClick({
      event,
      type: event.currentTarget.value,
    });
  };

  return (
    <DialogComponent title={title} onClose={onDialogClose}>
      <p className='confirmation-dialog-message'>{message}</p>
      <DialogActionsBar>
        <Button value={confirmValue} themeColor='primary' onClick={handleDialogClick}>
          {confirmLabel}
        </Button>
        <Button value='cancel' themeColor='base' onClick={handleDialogClick}>
          {label.CANCEL}
        </Button>
      </DialogActionsBar>
    </DialogComponent>
  );
});

export const DeleteDialog = (props) => (
  <ConfirmationDialog
    title={"Please confirm"}
    message={`${quote.DELETE_SELECTED_FILE} ${props?.editValue?.type === label.FOLDER ? label.FOLDER : label.FILE}? ${props.editValue.isDeleted ? quote.CANT_UNDO : ""}`}
    confirmLabel={label.DELETE}
    confirmValue='delete'
    onDialogClick={props.onDialogClick}
    onDialogClose={props.onDialogClose}
  />
);

export const RestoreDialog = (props) => (
  <ConfirmationDialog
    title={"Please confirm"}
    message={quote.RESTORE_SELECTED_FILE}
    confirmLabel={label.RESTORE}
    confirmValue='restore'
    onDialogClick={props.onDialogClick}
    onDialogClose={props.onDialogClose}
  />
);

/**
 * Dialog component for creating folders
 * @author Muskan Thakur
 */
export const FolderNameDialog = React.memo((props) => {
  const { defaultName, onDialogClose, title, inputLabel, isDox } = props;
  const [nameValue, setNameValue] = useState(getInitialName(defaultName, isDox));
  const [hasValidationError, setHasValidationError] = useState(false);
  const inputRef = useRef(null);
  const dialogRef = useRef(null);

  useClickOutsideDialog(onDialogClose);
  useInputFocus(inputRef);

  /**
   * Update folder name when default name prop changes
   */
  useEffect(() => {
    setNameValue(props.defaultName || "");
  }, [props.defaultName]);

  /**
   * Handle input change
   */
  const handleInputChange = (event) => {
    setNameValue(event.value);
    if (hasValidationError) {
      setHasValidationError(false);
    }
  };

  /**
   * Validate and submit folder name
   */
  const handleSubmit = () => {
    const trimmedValue = nameValue.trim();
    if (!trimmedValue) {
      setHasValidationError(true);
      getNotification(quote.FOLDER_NAME_EMPTY, notifyIcon.WARNING_ICON);
      return;
    }

    let finalValue = trimmedValue;
    if (isDox) {
      finalValue = `${trimmedValue}${label.DOX_EXT}`;
    }

    props.onDialogClick({
      type: "create",
      value: finalValue,
    });
  };

  /**
   * Handle Ctrl+S for saving
   * @param {object} event
   */
  const handleKeyDown = (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "s") {
      event.preventDefault();
      handleSubmit();
    } else if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  /** Add keyboard event listener to dialog for handling shortcuts */
  useEffect(() => {
    const dialogElement = document.querySelector(".k-dialog");
    if (dialogElement) {
      dialogElement.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      if (dialogElement) {
        dialogElement.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [nameValue]);

  const renderInput = () => {
    if (isDox) {
      return <DoxInput inputRef={inputRef} value={nameValue} onChange={handleInputChange} hasValidationError={hasValidationError} />;
    }
    return <Input ref={inputRef} value={nameValue} onChange={handleInputChange} className={hasValidationError ? "error" : ""} />;
  };

  const dialogTitle = title || tooltip.ADD_A_FOLDER;
  const buttonText = title ? button.SAVE : button.CREATE_FOLDER;
  const defaultLabel = isDox ? "" : label.ENTER_FOLDER_NAME;
  const labelText = inputLabel === undefined ? defaultLabel : inputLabel;

  return (
    <DialogComponent title={dialogTitle} onClose={onDialogClose} className={`folder-name-dialog-wrapper dialog-component ${isDox ? "dox-dialog-wrapper" : ""}`} ref={dialogRef}>
      <div className='k-content k-window-content k-dialog-content p-0'>
        <div className='input-label mb-1'>{labelText}</div>
        {renderInput()}
      </div>
      <DialogActionsBar className={isDox ? "dox-actions-bar" : ""}>
        {!isDox && <Button className="btn btn-ghost" onClick={onDialogClose}>{button.CANCEL}</Button>}
        <button onClick={handleSubmit} className={isDox ? "btn btn-primary flex-grow-0" : "btn btn-primary"}>{isDox ? button.SAVE : buttonText}</button>
      </DialogActionsBar>
    </DialogComponent>
  );
});
